import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FiArrowLeft, FiArrowRight, FiMail, FiSmartphone, FiAtSign, FiMessageSquare,
    FiLock, FiEye, FiEyeOff, FiShield
} from 'react-icons/fi';
import axiosInstance from '../axiosInstance';
import LoginStage from './login/LoginStage';
import CodeSlots from './login/CodeSlots';
import { classifyLoginIdentifier, identifierLooksLike, mmss } from './login/loginIdentifier';
import './Login.css';

// ─── The office login ────────────────────────────────────────────────────────
// ONE field — an email address or a mobile number — then a 6-digit code sent
// by email or by SMS (POST /auth/request-email-code | /auth/request-otp), then
// a short welcome while the app takes over. No password on the everyday path.
// The password door (POST /auth/login) is still rendered when the server does
// not advertise the code doors, and always at the break-glass /login?password=1
// — deliberately NOT linked from anywhere in the UI.

// The identifier a code was last successfully requested for, remembered per
// device so the daily login (24h token) is: open → send → type the code. A
// login identifier only — never the code itself. The old key held a phone.
const LAST_ID_KEY = 'staff_login_last_id';
const LEGACY_LAST_PHONE_KEY = 'staff_otp_last_phone';
const readLastId = () => {
    try { return localStorage.getItem(LAST_ID_KEY) || localStorage.getItem(LEGACY_LAST_PHONE_KEY) || ''; } catch { return ''; }
};
const storeLastId = (value) => {
    try { localStorage.setItem(LAST_ID_KEY, value); } catch {}
};

const reducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Hebrew copy for the code doors. The server deliberately answers every
// failure with a generic body (anti-enumeration), so the status is the signal.
const codeErrorMessage = (status) => {
    if (status === 429) return 'יותר מדי ניסיונות. נסו שוב בעוד כמה דקות.';
    if (status === 401) return 'הקוד שגוי או שפג תוקפו.';
    return 'הכניסה נכשלה. נסו שוב.';
};

// ─── STG multi-role test identities ──────────────────────────────────────────
// When one phone resolves to several test identities (TEST_LOGIN_ALIASES=1,
// staging only), verify-otp answers `needsSelection` + a picker, plus a
// reusable switchToken so hopping between identities doesn't burn the SMS
// throttle. The switch offer is remembered per TAB (sessionStorage): a
// SHORTLIST token, not a session — the server re-validates every pick — and
// none of this renders unless the server actually sent it, so production is
// untouched.
const SWITCH_KEY = 'staff_test_switch';
const readSwitchOffer = () => {
    try {
        const raw = sessionStorage.getItem(SWITCH_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.switchToken || !Array.isArray(parsed.options)) return null;
        if (!parsed.expiresAt || Date.now() >= parsed.expiresAt) return null;
        return parsed;
    } catch { return null; }
};
const storeSwitchOffer = (offer) => {
    try { sessionStorage.setItem(SWITCH_KEY, JSON.stringify(offer)); } catch {}
};
const clearSwitchOffer = () => {
    try { sessionStorage.removeItem(SWITCH_KEY); } catch {}
};

const ROLE_LABELS = {
    admin: 'מנהל מערכת',
    manager: 'מנהל',
    user: 'משתמש',
    apartment_manager: 'מנהל דירות',
    field_supervisor: 'מפקח שטח'
};
const roleLabel = (role) => ROLE_LABELS[role] || role || '';

// Where the sparks fly when the check lands (px from the centre).
const SPARKS = [[0, -60], [44, -42], [60, 2], [42, 44], [0, 60], [-44, 42], [-60, 0], [-42, -44]];

const Login = ({ onLoginSuccess }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Capability discovery: the bundle is built once and promoted, so which
    // doors exist is a SERVER fact (/auth/config), not a build-time flag.
    // Until it answers, render neither form (the field must not swap under a
    // typing user); a 2.5s deadline keeps a hung request from blocking the
    // login — deadline or failure ⇒ the password form, the old behaviour.
    const [doors, setDoors] = useState({ resolved: false, sms: false, email: false });

    // The step lives in HISTORY STATE so the browser back button returns
    // code → identifier instead of leaving the page. `from` (the return URL
    // ProtectedRoute stored) must ride along on every step navigation.
    const step = location.state?.loginStep || 'id'; // id | code | select
    const goToStep = (s) =>
        navigate(
            { pathname: location.pathname, search: window.location.search },
            { state: { loginStep: s, from: location.state?.from } }
        );

    const [identifier, setIdentifier] = useState(readLastId);
    const [sent, setSent] = useState(null); // { channel: 'sms'|'email', to }
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [rejections, setRejections] = useState(0);
    const [welcome, setWelcome] = useState(null); // { name }
    // The resend cooldown echoes the server's per-key throttle, so it belongs
    // to the identifier it was issued for — correcting a mistyped number must
    // not stay blocked by the wrong number's cooldown.
    const [cooldown, setCooldown] = useState({ left: 0, from: 60, key: '' });
    const codeRef = useRef(null);
    const leaveTimer = useRef(null);
    // The hand-over to the app, held while the welcome plays. If the page
    // unmounts first (Back during the welcome) it is run at once rather than
    // dropped: the token is already stored, the code already spent, and
    // dropping it would leave a half-logged-in user to start over.
    const pendingHandoff = useRef(null);

    // STG test-identity picker (never in production).
    const [selection, setSelection] = useState(null); // { token, options }
    const [switchOffer, setSwitchOffer] = useState(readSwitchOffer);
    const [switchDismissed, setSwitchDismissed] = useState(false);

    // Password door state (break-glass / code doors off).
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);

    const forcePassword = new URLSearchParams(window.location.search).get('password') === '1';
    const codeMode = doors.sms && !forcePassword;
    const pending = !doors.resolved && !forcePassword;
    const emailAllowed = doors.email;

    useEffect(() => {
        let cancelled = false;
        const deadline = setTimeout(() => { if (!cancelled) setDoors((d) => ({ ...d, resolved: true })); }, 2500);
        (async () => {
            try {
                const { data } = await axiosInstance.get('/auth/config');
                if (!cancelled) {
                    setDoors({ resolved: true, sms: data?.staffOtpLogin === true, email: data?.staffEmailLogin === true });
                }
            } catch {
                if (!cancelled) setDoors((d) => ({ ...d, resolved: true }));
            }
        })();
        return () => {
            cancelled = true;
            clearTimeout(deadline);
            clearTimeout(leaveTimer.current);
            const handoff = pendingHandoff.current;
            pendingHandoff.current = null;
            if (handoff) handoff();
        };
    }, []);

    useEffect(() => {
        if (cooldown.left <= 0) return undefined;
        const t = setTimeout(() => setCooldown((c) => ({ ...c, left: Math.max(0, c.left - 1) })), 1000);
        return () => clearTimeout(t);
    }, [cooldown.left]);

    // History state survives a reload but what was sent does not — a code step
    // with nothing in memory is a dead form, so return to the identifier step.
    useEffect(() => {
        if ((step === 'code' && !sent) || (step === 'select' && !selection)) {
            navigate(
                { pathname: location.pathname, search: window.location.search },
                { replace: true, state: { loginStep: 'id', from: location.state?.from } }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, sent, selection]);

    // A step change (including the browser back button) starts clean.
    useEffect(() => { setError(''); setNotice(''); }, [step]);

    // The code field is disabled while a code is checked — focus it again once typeable.
    useEffect(() => {
        if (step === 'code' && !busy) codeRef.current?.focus();
    }, [step, busy]);

    // Which way the card's content slides: forward to the code, back to the field.
    const prevStep = useRef(step);
    const back = prevStep.current !== 'id' && step === 'id';
    useEffect(() => { prevStep.current = step; }, [step]);

    // The ONE post-authentication sequence, shared by every door — all of
    // them answer the identical `{ token, user }` body.
    const finishLogin = (data) => {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        setWelcome({ name: data.user?.firstName || '' });
        // Let the welcome land before the app takes over.
        pendingHandoff.current = () => { if (onLoginSuccess) onLoginSuccess(data.user, data.token); };
        leaveTimer.current = setTimeout(() => {
            const handoff = pendingHandoff.current;
            pendingHandoff.current = null;
            if (handoff) handoff();
        }, reducedMotion() ? 150 : 1250);
    };

    const cooldownActive = (key) => cooldown.left > 0 && cooldown.key === key;

    // ─── code doors ──────────────────────────────────────────────────────────
    const requestCode = async ({ again = false } = {}) => {
        setError('');
        setNotice('');
        const target = again && sent
            ? { kind: sent.channel === 'email' ? 'email' : 'phone', value: sent.to }
            : classifyLoginIdentifier(identifier);

        if (target.kind === 'empty') return;
        if (target.kind === 'email' && !emailAllowed) {
            setError('כניסה עם אימייל אינה זמינה כרגע. הזינו מספר טלפון נייד.');
            return;
        }
        if (target.kind !== 'email' && target.kind !== 'phone') {
            setError(emailAllowed
                ? 'הזינו כתובת אימייל או מספר טלפון נייד תקינים.'
                : 'הזינו מספר טלפון נייד תקין.');
            return;
        }

        const channel = target.kind === 'email' ? 'email' : 'sms';
        const key = `${channel}:${target.value}`;
        setBusy(true);
        try {
            if (channel === 'email') {
                await axiosInstance.post('/auth/request-email-code', { email: target.value });
            } else {
                await axiosInstance.post('/auth/request-otp', { phone: target.value });
            }
            // The server ALWAYS answers a generic success, registered or not —
            // advancing is not a signal that the identifier exists.
            if (!again) storeLastId(identifier.trim());
            setSent({ channel, to: target.value });
            setCode('');
            setCooldown({ left: 60, from: 60, key });
            if (again) setNotice('שלחנו קוד חדש. הקוד הקודם כבר לא בתוקף.');
            if (step !== 'code') goToStep('code');
        } catch (err) {
            const status = err.response?.status;
            if (status === 429) {
                const wait = Number(err.response?.data?.retryAfterSec) || 60;
                setCooldown({ left: wait, from: wait, key });
                setError(wait <= 90
                    ? `כבר שלחנו קוד. אפשר לבקש חדש בעוד ${wait} שניות.`
                    : 'שלחנו כבר כמה קודים. נסו שוב מאוחר יותר.');
            } else {
                setError('לא הצלחנו לשלוח קוד. נסו שוב.');
            }
        } finally {
            setBusy(false);
        }
    };

    const verify = async (value = code) => {
        if (busy || value.length !== 6 || !sent) return;
        setError('');
        setNotice('');
        setBusy(true);
        try {
            // The body key MUST stay exactly `otp` — the audit logger redacts
            // that key by name, so renaming it writes login codes to the log.
            const { data } = sent.channel === 'email'
                ? await axiosInstance.post('/auth/verify-email-code', { email: sent.to, otp: value })
                : await axiosInstance.post('/auth/verify-otp', { phone: sent.to, otp: value });
            if (data.needsSelection) {
                // STG only: several test identities behind one phone — pick.
                setSelection({ token: data.selectToken, options: data.options || [] });
                if (data.switchToken) {
                    const ttlMs = (Number(data.switchTtlMinutes) || 30) * 60 * 1000;
                    storeSwitchOffer({ switchToken: data.switchToken, options: data.options || [], expiresAt: Date.now() + ttlMs });
                    setSwitchOffer(readSwitchOffer());
                }
                setBusy(false);
                goToStep('select');
                return;
            }
            if (data.success) {
                finishLogin(data);
                return;
            }
            throw Object.assign(new Error('login failed'), { response: { status: 401 } });
        } catch (err) {
            setCode('');
            setError(codeErrorMessage(err.response?.status));
            setRejections((n) => n + 1);
            setBusy(false);
        }
    };

    const onCodeChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(digits);
        if (error) setError('');
        if (digits.length === 6) verify(digits); // pasted or autofilled — no extra tap
    };

    // From the code step: back to the field. `fresh` empties it — the
    // "SMS doesn't arrive? use your email" path.
    const changeIdentifier = ({ fresh = false } = {}) => {
        if (fresh) setIdentifier('');
        setCode('');
        navigate(-1);
    };

    // ─── STG picker ──────────────────────────────────────────────────────────
    const handleSelectIdentity = async (token, userId, viaSwitch) => {
        setBusy(true);
        setError('');
        try {
            const { data } = await axiosInstance.post('/auth/select', { selectToken: token, userId });
            if (data.success) { finishLogin(data); return; }
        } catch {
            if (viaSwitch) { clearSwitchOffer(); setSwitchOffer(null); }
            setSelection(null);
            setError('תוקף הבחירה פג — התחברו מחדש עם קוד.');
            if (step !== 'id') goToStep('id');
        }
        setBusy(false);
    };

    const renderIdentityOptions = (options, token, viaSwitch) => (
        <div className="sl-identities" role="list">
            {(options || []).map((o) => (
                <button
                    key={o.userId}
                    type="button"
                    role="listitem"
                    className="sl-identity"
                    disabled={busy}
                    onClick={() => handleSelectIdentity(token, o.userId, viaSwitch)}
                >
                    <span className="sl-identity-name">
                        {`${o.firstName || ''} ${o.lastName || ''}`.trim() || o.username || '—'}
                        {o.viaTestAlias && <span className="sl-identity-badge">בדיקות</span>}
                    </span>
                    <span className="sl-identity-meta">
                        {roleLabel(o.role)}
                        {o.orgName ? ` · ${o.orgName}` : ''}
                        {o.label ? ` · ${o.label}` : ''}
                    </span>
                </button>
            ))}
        </div>
    );

    // ─── password door ───────────────────────────────────────────────────────
    const handleLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const { data } = await axiosInstance.post('/auth/login', { ...loginData, rememberMe });
            if (data.success) { finishLogin(data); return; }
            setError('ההתחברות נכשלה. נסו שוב.');
        } catch (err) {
            setError(err.response?.data?.message || 'ההתחברות נכשלה. נסו שוב.');
        }
        setBusy(false);
    };

    const switchOfferActive =
        codeMode && step === 'id' && !switchDismissed && switchOffer && Date.now() < switchOffer.expiresAt;

    const looks = identifierLooksLike(identifier, { emailAllowed });
    const FieldIcon = looks === 'phone' ? FiSmartphone : looks === 'email' ? FiMail : FiAtSign;
    const byEmail = sent?.channel === 'email';
    const resendKey = sent ? `${sent.channel}:${sent.to}` : '';
    const resendLeft = cooldownActive(resendKey) ? cooldown.left : 0;
    const idKind = classifyLoginIdentifier(identifier).kind;
    const idKey = idKind === 'email' || idKind === 'phone'
        ? `${idKind === 'email' ? 'email' : 'sms'}:${classifyLoginIdentifier(identifier).value}`
        : '';
    const idCooldown = idKey && cooldownActive(idKey) ? cooldown.left : 0;

    const errorNote = error ? (
        <div id="sl-error" className="sl-note is-error" role="alert">{error}</div>
    ) : null;

    let body;
    if (welcome) {
        body = <Welcome name={welcome.name} />;
    } else if (pending) {
        body = (
            <div className="sl-pending" aria-busy="true">
                <span className="sl-spinner is-dark" role="status" aria-label="טוען" />
            </div>
        );
    } else if (!codeMode) {
        body = (
            <div key="password" className="sl-step">
                <h2 className="sl-title">כניסה למערכת</h2>
                <p className="sl-sub">הזינו את האימייל והסיסמה שלכם.</p>
                <form onSubmit={handleLogin} className="sl-form" noValidate>
                    <div className="sl-field">
                        <label htmlFor="sl-email" className="sl-label">אימייל</label>
                        <div className="sl-input-wrap">
                            <input
                                id="sl-email"
                                type="email"
                                name="email"
                                dir="ltr"
                                autoComplete="username"
                                autoFocus
                                className="sl-input"
                                placeholder="name@company.co.il"
                                value={loginData.email}
                                onChange={(e) => { setLoginData({ ...loginData, email: e.target.value }); setError(''); }}
                                required
                            />
                            <span className="sl-input-icon"><FiMail /></span>
                        </div>
                    </div>
                    <div className="sl-field">
                        <label htmlFor="sl-password" className="sl-label">סיסמה</label>
                        <div className="sl-input-wrap">
                            <input
                                id="sl-password"
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                dir="ltr"
                                autoComplete="current-password"
                                className="sl-input has-toggle"
                                placeholder="••••••••"
                                value={loginData.password}
                                onChange={(e) => { setLoginData({ ...loginData, password: e.target.value }); setError(''); }}
                                required
                            />
                            <span className="sl-input-icon"><FiLock /></span>
                            <button
                                type="button"
                                className="sl-eye"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                            >
                                {showPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                    </div>
                    <label className="sl-check">
                        <input type="checkbox" checked={rememberMe} onChange={() => setRememberMe((v) => !v)} />
                        <span>זכור אותי</span>
                    </label>
                    {errorNote}
                    <button type="submit" className="sl-cta" disabled={busy || !loginData.email || !loginData.password}>
                        {busy ? <><span className="sl-spinner" /> מתחברים…</> : <>כניסה <FiArrowLeft className="sl-cta-arrow" /></>}
                    </button>
                </form>
            </div>
        );
    } else if (step === 'select' && selection) {
        body = (
            <div key="select" className="sl-step">
                <h2 className="sl-title">בחרו זהות</h2>
                <p className="sl-sub">המספר מקושר לכמה זהויות בדיקה.</p>
                {renderIdentityOptions(selection.options, selection.token, false)}
                {errorNote}
                <button type="button" className="sl-link" disabled={busy} onClick={() => navigate(-1)}>
                    <FiArrowRight /> חזרה
                </button>
            </div>
        );
    } else if (step === 'code' && sent) {
        body = (
            <div key="code" className="sl-step">
                <button type="button" className="sl-back" onClick={() => changeIdentifier()}>
                    <FiArrowRight />
                    {byEmail ? 'אימייל אחר' : 'מספר אחר'}
                </button>

                <div className="sl-code-head">
                    <span className="sl-code-badge">
                        <span className="sl-ping" aria-hidden="true" />
                        {byEmail ? <FiMail /> : <FiMessageSquare />}
                    </span>
                    <h2 className="sl-title">{byEmail ? 'בדקו את המייל' : 'בדקו את ההודעות'}</h2>
                </div>
                <p className="sl-sub">
                    {byEmail ? 'אם הכתובת רשומה במערכת, שלחנו אליה' : 'אם המספר רשום במערכת, שלחנו אליו ב-SMS'} קוד בן 6 ספרות:{' '}
                    <bdi dir="ltr" className="sl-sent-to">{sent.to}</bdi>. הקוד בתוקף ל-5 דקות.
                </p>

                <form onSubmit={(e) => { e.preventDefault(); verify(); }} className="sl-form" noValidate>
                    <label htmlFor="sl-code" className="sl-sr-only">קוד כניסה</label>
                    <CodeSlots
                        ref={codeRef}
                        id="sl-code"
                        value={code}
                        onChange={onCodeChange}
                        disabled={busy}
                        state={error ? 'error' : busy ? 'checking' : 'idle'}
                        shakeKey={rejections}
                        describedBy={error ? 'sl-error' : undefined}
                    />
                    {errorNote}
                    {notice && !error && <div className="sl-note is-ok" role="status">{notice}</div>}
                    <button type="submit" className="sl-cta" disabled={busy || code.length !== 6}>
                        {busy ? <><span className="sl-spinner" /> בודקים…</> : <>כניסה <FiArrowLeft className="sl-cta-arrow" /></>}
                    </button>
                </form>

                <div className="sl-resend">
                    {byEmail ? 'הקוד לא הגיע? כדאי להציץ גם בתיקיית הספאם. ' : 'הקוד לא הגיע? '}
                    {resendLeft > 0 ? (
                        <span className="sl-resend-wait">אפשר לשלוח קוד חדש בעוד {mmss(resendLeft)}</span>
                    ) : (
                        <button type="button" className="sl-inline-btn" disabled={busy} onClick={() => requestCode({ again: true })}>
                            שלחו קוד חדש
                        </button>
                    )}
                    {resendLeft > 0 && (
                        <div className="sl-resend-bar" aria-hidden="true">
                            <span style={{ width: `${Math.min(100, (resendLeft / (cooldown.from || 60)) * 100)}%` }} />
                        </div>
                    )}
                </div>

                {!byEmail && emailAllowed && (
                    <button type="button" className="sl-alt" onClick={() => changeIdentifier({ fresh: true })}>
                        <FiMail />
                        <span>לא מקבלים SMS? <b>היכנסו עם כתובת האימייל</b></span>
                    </button>
                )}
            </div>
        );
    } else {
        body = (
            <div key="id" className={`sl-step${back ? ' is-back' : ''}`}>
                {switchOfferActive ? (
                    <>
                        <h2 className="sl-title">החלפת זהות</h2>
                        <p className="sl-sub">מצב בדיקות — בחרו זהות או התחברו עם קוד.</p>
                        {renderIdentityOptions(switchOffer.options, switchOffer.switchToken, true)}
                        {errorNote}
                        <button type="button" className="sl-link" disabled={busy} onClick={() => setSwitchDismissed(true)}>
                            התחברות עם קוד
                        </button>
                    </>
                ) : (
                    <>
                        <h2 className="sl-title">כניסה למערכת</h2>
                        <p className="sl-sub">
                            {emailAllowed
                                ? 'הזינו אימייל או מספר נייד — נשלח אליכם קוד כניסה חד-פעמי.'
                                : 'הזינו את מספר הנייד שלכם — נשלח אליכם קוד כניסה חד-פעמי ב-SMS.'}
                        </p>
                        <form onSubmit={(e) => { e.preventDefault(); requestCode(); }} className="sl-form" noValidate>
                            <div className="sl-field">
                                <label htmlFor="sl-id" className="sl-label">
                                    {emailAllowed ? 'אימייל או טלפון נייד' : 'טלפון נייד'}
                                </label>
                                <div className="sl-input-wrap">
                                    <input
                                        id="sl-id"
                                        type={emailAllowed ? 'text' : 'tel'}
                                        inputMode={looks === 'phone' ? 'tel' : looks === 'email' ? 'email' : 'text'}
                                        autoComplete="username"
                                        autoCapitalize="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        autoFocus
                                        dir="ltr"
                                        className="sl-input"
                                        placeholder={emailAllowed ? 'name@company.co.il  ·  050-0000000' : '050-0000000'}
                                        value={identifier}
                                        onChange={(e) => { setIdentifier(e.target.value); if (error) setError(''); }}
                                        aria-invalid={!!error}
                                        aria-describedby={error ? 'sl-error' : undefined}
                                    />
                                    <span className="sl-input-icon">
                                        <FieldIcon key={looks} className="sl-icon-in" />
                                    </span>
                                </div>
                            </div>
                            {errorNote}
                            <button type="submit" className="sl-cta" disabled={busy || !identifier.trim() || idCooldown > 0}>
                                {busy
                                    ? <><span className="sl-spinner" /> שולחים…</>
                                    : idCooldown > 0
                                        ? <>אפשר לשלוח שוב בעוד {mmss(idCooldown)}</>
                                        : <>שלחו לי קוד <FiArrowLeft className="sl-cta-arrow" /></>}
                            </button>
                        </form>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="sl-root" dir="rtl">
            <main className="sl-main">
                <div className="sl-card">
                    <div className="sl-card-bar" aria-hidden="true" />
                    {body}
                    {!welcome && (
                        <div className="sl-card-foot">
                            <FiShield />
                            חיבור מאובטח
                        </div>
                    )}
                </div>
            </main>
            <LoginStage />
        </div>
    );
};

// The success moment — the check draws, a ring pulses out, sparks fly. It
// plays once and the app takes over.
function Welcome({ name }) {
    return (
        <div className="sl-step sl-welcome" role="status">
            <div className="sl-welcome-mark">
                <span className="sl-welcome-ring" aria-hidden="true" />
                {SPARKS.map(([dx, dy], i) => (
                    <span key={i} className="sl-spark" style={{ '--dx': `${dx}px`, '--dy': `${dy}px` }} aria-hidden="true" />
                ))}
                <span className="sl-welcome-pop">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path className="sl-welcome-check" pathLength="100" d="M5 12.5l4.3 4.3L19 7.2" />
                    </svg>
                </span>
            </div>
            <h2 className="sl-title">ברוכים הבאים{name ? `, ${name}` : ''}</h2>
            <p className="sl-sub">נכנסים למערכת…</p>
        </div>
    );
}

export default Login;
